import { writable } from "svelte/store";
import { config } from "../config.js";
import { get } from "../services/http.js";

function productsStore() {
  let page = 0;
  let limit = 24;
  let loadedItems = {};
  let reset = false;
  // this will be used to scroll to
  let productPageY = 0;
  let filters = {
    type: null,
    active: null,
    sortBy: null,
    query: null,
    search: null,
    admin: false,
  };

  /* This builds the URL to fetch product list with */
  const getUrl = () => {
    let url = `${config.apiUrl}/products?limit=${limit}&page=${page}`;

    // Product Type
    if (filters.type && filters.type !== "null") {
      url += `&product-type-id=${filters.type}`;
    }

    // Public/Private
    if (filters.active) {
      url += `&active=${filters.active}`;
    }

    // Hide out of stock products
    if (!filters.admin) {
      url += "&in-stock-only=1";
    }

    // Sorting
    if (filters.sortBy) {
      url += `&sort-by=${filters.sortBy}`;
    }

    // Query string
    if (filters.query) {
      url += `&${filters.query.join("&")}`;
    }

    // Search Term
    if (filters.search) {
      url += `&query=${filters.search}`;
    }

    // Search Term
    if (filters.sale) {
      url += `&show-sale=1`;
    }

    return url;
  };

  const load = async () => {
    if (page > 0 && !reset) {
      // load whatever was already loaded
      set(loadedItems);
    } else {
      productPageY = 0;
      page = 0;
      let url = getUrl();
      let data = await get(url, false); // does not need auth

      if (
        data.filters &&
        data.filters.attributes &&
        data.filters.attributes["Piece Count Range"]
      ) {
        data.filters.attributes["Piece Count Range"].sort(sortPieceCount);
      }
      loadedItems = data;
      set({ ...data, loading: false });
    }
    // scroll to the position the user was last at
    scrollTo(0, productPageY);
  };

  const loadMore = async (items) => {
    let url = getUrl();
    // does not need auth
    await get(url, false).then((data) => {
      let more = data.products || [];
      data.products = [...items, ...more];
      loadedItems = data;
      set(data);
    });
  };

  const sortPieceCount = (a, b) => {
    if (parseInt(a.value.split(" - ")[0]) < parseInt(b.value.split(" - ")[0]))
      return -1;
    return 1;
  };

  // Reset filters, then assign new filters
  const setFilters = async (newFilters) => {
    reset = newFilters.type !== filters.type;
    for (var k in filters) {
      filters[k] = null;
    }

    Object.assign(filters, newFilters);
  };

  const { subscribe, set, update } = writable({
    products: [],
    filters: { categories: [], attributes: [] },
    loading: true,
  });

  return {
    subscribe,
    loadAdminProducts: async (l) => {
      // admin page uses this
      limit = l;
      filters.admin = true;
      load();
    },
    reload: async () => {
      load();
    },
    loadMore: (items) => {
      page++;
      loadMore(items);
    },
    loadAll: async (total) => {
      let tmpLimit = limit;
      limit = total;
      await load();
      limit = tmpLimit;
    },
    sortBy: (sortQuery) => {
      filters.sortBy = sortQuery;
      load();
    },
    setFilters: async (filters) => {
      await setFilters(filters);
      load();
    },
    setLimit: (newLimit) => {
      limit = newLimit;
      load();
    },
    setProductPageY: (pos) => {
      productPageY = pos;
    },
    getNextProduct: (currentProduct) => {
      // gets the previous/next product in the list for better admin navigation
      let nextProduct;
      let prevProduct;
      subscribe((p) => {
        let idx = p.products.findIndex((i) => i.id === currentProduct.id);
        if (idx + 1 === p.products.length) {
          // currently at the end of the list, get the first product
          nextProduct = p.products[0];
        } else {
          nextProduct = p.products[idx + 1];
        }
        if (idx === 0) {
          // currently at the start of the list, get the last product
          prevProduct = p.products[p.products.length - 1];
        } else {
          prevProduct = p.products[idx - 1];
        }
      });
      return { next: nextProduct, prev: prevProduct };
    },
    getSubscriptions: async () => {
      setFilters({
        type: 5,
        active: 1,
        sortBy: "price-asc",
      });
      let url = getUrl();
      return await get(url, false)
        .then((data) => {
          let subs = {};
          for (let p of data.products) {
            // subscription titles should have a hyphen in the title
            let title = p.title.split("-")[0] || p.title;
            title = title.trim();

            if (!subs[title]) {
              subs[title] = p;
              p.subscription_title = title;
            }

            if (p.title.indexOf("Monthly") > -1) {
              subs[title].monthly_price = p.price;
              subs[title].month_product_sku = p.sku;
              subs[title].monthly_id = p.id;
            } else {
              subs[title].yearly_price = p.price;
              subs[title].year_product_sku = p.sku;
              subs[title].yearly_id = p.id;
            }

            // rename title
            subs[title].title = title;
          }
          return Object.values(subs);
        })
        .then((products) => {
          /*
          hacky but they need to be in a certain order
        */
          let local = products[0];
          products.shift();
          products.push(local);
          return products;
        });
    },
  };
}

export let products = productsStore();
